import React, { useEffect, useMemo, useRef } from "react";
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Typography,
} from "antd";
import {
  BankOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  DesktopOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  LockOutlined,
  MailOutlined,
  NumberOutlined,
  PhoneOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { TextArea } = Input;

const iconForField = (field) => {
  const iconStyle = { color: "rgba(100, 116, 139, 0.85)" };

  switch (field.name) {
    case "name":
      return <BankOutlined style={iconStyle} />;
    case "code":
      return <UserOutlined style={iconStyle} />;
    case "email":
      return <MailOutlined style={iconStyle} />;
    case "phone":
      return <PhoneOutlined style={iconStyle} />;
    case "address":
      return <EnvironmentOutlined style={iconStyle} />;
    case "city":
      return <TeamOutlined style={iconStyle} />;
    case "state":
      return <TeamOutlined style={iconStyle} />;
    case "host_name":
      return <DesktopOutlined style={iconStyle} />;
    default:
      switch (field.type) {
        case "email":
          return <MailOutlined style={iconStyle} />;
        case "password":
          return <LockOutlined style={iconStyle} />;
        case "tel":
          return <PhoneOutlined style={iconStyle} />;
        case "date":
          return <CalendarOutlined style={iconStyle} />;
        case "textarea":
          return <FileTextOutlined style={iconStyle} />;
        case "number":
          return <NumberOutlined style={iconStyle} />;
        case "money":
          return <DollarOutlined style={iconStyle} />;
        case "select":
          return <SearchOutlined style={iconStyle} />;
        case "checkbox":
          return <CheckSquareOutlined style={iconStyle} />;
        default:
          return <UserOutlined style={iconStyle} />;
      }
  }
};

const buildRules = (field) => {
  const rules = [];

  if (field.required) {
    rules.push({
      required: true,
      message: `${field.label} is required`,
    });
  }

  if (field.type === "email") {
    rules.push({ type: "email", message: "Invalid email address" });
  }

  if (field.type === "number") {
    // A number field renders a native number input, whose value is always a
    // string -- and "" once it is cleared. A rule with `type: "number"` would
    // reject every one of those as "not a number", so the bounds are checked
    // against a parsed copy instead.
    rules.push({
      validator: (_, value) => {
        if (value === "" || value === null || value === undefined) {
          // Emptiness is the required rule's business, not this one's.
          return Promise.resolve();
        }

        const parsedValue = Number(value);

        if (Number.isNaN(parsedValue)) {
          return Promise.reject(new Error(`${field.label} must be a number`));
        }
        if (field.integer && !Number.isInteger(parsedValue)) {
          return Promise.reject(new Error(`${field.label} must be a whole number`));
        }
        if (field.min !== undefined && parsedValue < field.min) {
          return Promise.reject(new Error(`${field.label} must be at least ${field.min}`));
        }
        if (field.max !== undefined && parsedValue > field.max) {
          return Promise.reject(new Error(`${field.label} must be at most ${field.max}`));
        }

        return Promise.resolve();
      },
    });
  }

  if (field.minLength) {
    rules.push({
      min: field.minLength,
      message: `Must be at least ${field.minLength} characters`,
    });
  }

  if (field.maxLength) {
    rules.push({
      max: field.maxLength,
      message: `Must be at most ${field.maxLength} characters`,
    });
  }

  if (field.validation) {
    rules.push({
      validator: (_, value) => {
        const validation = field.validation({
          required: field.required,
        });
        return validation.validate(value);
      },
    });
  }

  return rules;
};

const normalizeWidth = (field) => {
  if (field.fullWidth) return 24;
  if (!field.width) return 24;
  return Math.min(24, field.width);
};

// A `group` field is a panel, not a control: it owns nested fields plus an
// optional `extra` control rendered in its header. Everything that walks the
// field list to reach actual inputs -- initial values, the onChange map, the
// submit-time formatting -- has to see through it, so they all walk this.
const flattenFields = (fields) =>
  (fields || []).flatMap((field) =>
    field.type === "group"
      ? [...(field.extra ? [field.extra] : []), ...flattenFields(field.fields)]
      : [field]
  );

const CustomForm = ({
  fields,
  onSubmit,
  initialValues = {},
  formResetKey,
  buttonText = "Submit",
  title,
  subtitle,
  loading = false,
  compact = false,
  footerBordered = false,
  onCancel,
}) => {
  const [form] = Form.useForm();

  const leafFields = useMemo(() => flattenFields(fields), [fields]);

  const normalizedInitialValues = useMemo(() => {
    const values = { ...(initialValues || {}) };

    leafFields.forEach((field) => {
      if (field.type === "date" && values[field.name]) {
        const parsedValue = dayjs(values[field.name]);
        values[field.name] = parsedValue.isValid() ? parsedValue : null;
      }
    });

    return values;
  }, [leafFields, initialValues]);

  const latestInitialValuesRef = useRef(normalizedInitialValues);

  useEffect(() => {
    latestInitialValuesRef.current = normalizedInitialValues;
  }, [normalizedInitialValues]);

  useEffect(() => {
    const values = latestInitialValuesRef.current || {};

    if (Object.keys(values).length === 0) {
      form.resetFields();
      return;
    }

    form.setFieldsValue(values);
  }, [form, formResetKey]);

  const fieldMap = useMemo(
    () =>
      leafFields.reduce((acc, field) => {
        acc[field.name] = field;
        return acc;
      }, {}),
    [leafFields]
  );

  const handleValuesChange = (changedValues, allValues) => {
    Object.entries(changedValues).forEach(([name, value]) => {
      const field = fieldMap[name];
      if (field?.onChange) {
        field.onChange(value, null, {
          setFieldValue: (fieldName, fieldValue) => {
            form.setFieldsValue({ [fieldName]: fieldValue });
          },
          values: allValues,
        });
      }
    });
  };

  const handleFinish = async (values) => {
    const payload = { ...values };

    leafFields.forEach((field) => {
      if (field.type === "date") {
        const value = payload[field.name];
        payload[field.name] = value ? dayjs(value).format("YYYY-MM-DD") : value;
      }
    });

    await onSubmit(payload, form);
  };

  const renderControl = (field) => {
    const disabled = field.disabled || loading;
    const commonProps = {
      disabled,
      placeholder: field.placeholder,
      suffix: field.endAdornment,
    };

    if (field.type === "checkbox") {
      return <Checkbox disabled={disabled}>{field.label}</Checkbox>;
    }

    if (field.type === "switch") {
      return <Switch disabled={disabled} />;
    }

    if (field.type === "select") {
      return (
        <Select
          placeholder={field.placeholder || `Select ${field.label}`}
          options={(field.options || []).map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          disabled={disabled}
          showSearch={Boolean(field.showSearch)}
          optionFilterProp="label"
          filterOption={(input, option) =>
            String(option?.label || "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
      );
    }

    if (field.type === "date") {
      return (
        <DatePicker
          format="YYYY-MM-DD"
          style={{ width: "100%" }}
          placeholder={field.placeholder || `Select ${field.label}`}
          disabled={disabled}
          allowClear
        />
      );
    }

    if (field.type === "textarea") {
      return <TextArea rows={field.rows || 5} {...commonProps} />;
    }

    if (field.type === "number") {
      return (
        <Input
          type="number"
          // So the browser's own spinner and validation agree with the rules
          // above instead of offering values the form will then reject.
          min={field.min}
          max={field.max}
          step={field.integer ? 1 : field.step}
          prefix={field.icon === false ? null : field.icon || iconForField(field)}
          {...commonProps}
        />
      );
    }

    if (field.type === "password") {
      return <Input.Password prefix={field.icon === false ? null : field.icon || iconForField(field)} {...commonProps} />;
    }

    return (
      <Input
        type={field.type || "text"}
        prefix={field.icon === false ? null : field.icon || iconForField(field)}
        {...commonProps}
      />
    );
  };

  // Boolean controls carry their own text (the checkbox's own label, the
  // switch's heading in a group), so a Form.Item label above them would say it
  // twice.
  const isBooleanControl = (field) => field.type === "checkbox" || field.type === "switch";

  const renderField = (field) => {
    if (field.type === "group") {
      return (
        <Col xs={24} key={field.name}>
          <div className="custom-form-group">
            <div className="custom-form-group__header">
              <div>
                <Typography.Text strong>{field.title}</Typography.Text>
                {field.subtitle && (
                  <div className="custom-form-group__subtitle">{field.subtitle}</div>
                )}
              </div>
              {field.extra && (
                <div className="custom-form-group__extra">
                  <span className="custom-form-group__extra-label">{field.extra.label}</span>
                  <Form.Item name={field.extra.name} valuePropName="checked" noStyle>
                    {renderControl(field.extra)}
                  </Form.Item>
                </div>
              )}
            </div>
            <Row gutter={[16, 4]}>{(field.fields || []).map(renderField)}</Row>
          </div>
        </Col>
      );
    }

    const span = compact ? 24 : normalizeWidth(field);

    return (
      <Col xs={24} sm={24} md={span} key={field.name}>
        <Form.Item
          label={isBooleanControl(field) ? null : field.label}
          name={field.name}
          valuePropName={isBooleanControl(field) ? "checked" : "value"}
          rules={buildRules(field)}
        >
          {renderControl(field)}
        </Form.Item>
      </Col>
    );
  };

  return (
    <div className="custom-form">
      {(title || subtitle) && (
        <>
          <Space orientation="vertical" size={4} style={{ width: "100%" }}>
            {title && (
              <Typography.Title level={5} style={{ margin: 0 }}>
                {title}
              </Typography.Title>
            )}
            {subtitle && <Typography.Text type="secondary">{subtitle}</Typography.Text>}
          </Space>
          <Divider style={{ margin: "16px 0 20px" }} />
        </>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        onValuesChange={handleValuesChange}
        disabled={loading}
        initialValues={normalizedInitialValues}
      >
        <Row gutter={[16, 4]}>{fields.map(renderField)}</Row>

        <div
          className={footerBordered ? "custom-form__footer custom-form__footer--bordered" : "custom-form__footer"}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            marginTop: footerBordered ? 4 : 8,
            flexDirection: compact || !onCancel ? "column-reverse" : "row",
          }}
        >
          {onCancel && (
            <Button onClick={onCancel} block={compact} disabled={loading}>
              Cancel
            </Button>
          )}
          <Button type="primary" htmlType="submit" block={compact} loading={loading}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CustomForm;
